// What a missed emergency can still be given: a call back, or a dismissal. Both ACKNOWLEDGE it, so it
// leaves the missed list on every console — the server is the record that someone took it, not this
// browser (a per-browser memory is what let a nine-day-old fall come back on every page load).
//
// INSTANT, not after the round-trip. The row leaves on the click and a call back starts on the click;
// the acknowledgement goes to the server behind it. If the server refuses, the row comes back with
// the reason beside it, so a dismissal that never reached the server never looks done.

import { robotsApi } from '@/services/api/domains/robots.api'
import { removeRobotAlertOptimistically } from '@/services/api/optimistic-alert-removal'
import type { RobotAlertItem } from '@/services/api/robot-alerts-store'
import { clearSosAnswerNotice, setSosAnswerNotice } from '@/services/api/sos-answer-notices'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { beginSosCall } from '../SosCall/sos-call-store'
import { useSosAnswerNotices } from './useSosAnswerNotices'

export type MissedAction = 'callBack' | 'dismiss'

export interface MissedEmergencyActions {
  /** Why the last action on an alert did not go through, by alert id. Shown on that alert's row. */
  notices: ReadonlyMap<string, string>
  act(item: RobotAlertItem, action: MissedAction, call?: { label: string }): void
}

/** Prefer the backend's own wording for a failure; fall back only for opaque throws. */
function failureMessage(err: unknown, fallback: string): string {
  return (err instanceof Error ? err.message.trim() : '') || fallback
}

export function useMissedEmergencyActions(): MissedEmergencyActions {
  const { t } = useTranslation('console')
  const notices = useSosAnswerNotices()

  const act = useCallback(
    (item: RobotAlertItem, action: MissedAction, call?: { label: string }) => {
      const id = item.alertId

      clearSosAnswerNotice(id)

      // The call goes out on the click itself. The microphone prompt must come from the click: a
      // browser refuses a getUserMedia that waited for a request. Nothing is recorded when no call
      // can be placed, so the row stays and says why.
      if (action === 'callBack' && call && !beginSosCall({ robotId: item.robotId, label: call.label, alertId: id })) {
        setSosAnswerNotice(
          id,
          t('alerts.sos.callBusy', 'No call was placed — another emergency call is already in progress.'),
        )

        return
      }

      const settle = removeRobotAlertOptimistically(id)

      void robotsApi.acknowledgeAlert(id).then(
        () => settle(true),
        (err: unknown) => {
          // Back in the list: nothing was recorded, so it is still missed and still answerable. A
          // call already placed carries on; the operator dismisses the row once they are done.
          settle(false)
          setSosAnswerNotice(id, failureMessage(err, t('alerts.sos.failed', "Couldn't record your answer. Try again.")))
        },
      )
    },
    [t],
  )

  return { notices, act }
}
